/**
 * Manifest Loader — INIT-2026-Q3-SCENE-HIERARCHY Work Package C.
 *
 * Integration glue between the adapter registry and the Zustand store.
 * Calls `selectAdapterForSceneItem` → `adapter.buildManifest` and wraps
 * any unexpected exceptions into a typed `AdapterResult`.
 *
 * No domain-specific branching in this file (DESIGN_POLICY §5).
 * All domain differences live inside adapter files.
 */

import type { SceneItem } from "@/sdk-stub/gtwin-sdk/types";
import type { AdapterResult, DomainAdapterContext } from "./adapters/DomainAdapter";
import {
  selectAdapterForSceneItem,
  inferDomainFromSceneItem,
} from "./adapters/registry";

// ─────────────────────────────────────────────────────────────────────────────
// § 1  Core loader function
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Loads the Tier-2 manifest for a SceneItem.
 *
 * 1. Calls `selectAdapterForSceneItem` to find the matching adapter.
 * 2. If no adapter → returns `{ ok: false, error: { kind: "not-implemented" } }`.
 * 3. Delegates to `adapter.buildManifest(item, asset, context)`.
 * 4. Any unexpected exception is caught and returned as `{ kind: "fetch-failed" }`.
 *
 * Never throws. Always resolves.
 *
 * @param item    - The SceneItem to build a manifest for.
 * @param asset   - Identity triple for the manifest root.
 * @param context - Optional IO dependencies (fetchJson, signal).
 */
export async function loadTier2Manifest(
  item: SceneItem,
  asset: { layerId: string; assetNodeId: string; gtwinId: string },
  context: DomainAdapterContext = {},
): Promise<AdapterResult> {
  try {
    const adapter = selectAdapterForSceneItem(item);

    if (!adapter) {
      const domain = inferDomainFromSceneItem(item);
      return {
        ok: false,
        error: {
          kind: "not-implemented",
          domain,
          message: `No adapter registered for domain "${domain}" that recognises this SceneItem.`,
        },
      };
    }

    return await adapter.buildManifest(item, asset, context);
  } catch (cause) {
    return {
      ok: false,
      error: { kind: "fetch-failed", cause },
    };
  }
}
